import { CompanyDescription } from '@aibees/elephant/feature/search-drawer/components/company-description';
import {
  useWorkHistoryCurrentRoles,
  useWorkHistoryFullHistory,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history';
import { getJobDuration } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history/utils';
import { JobHistory } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/person-job-info/components/job-history';
import { humanReadablePersonLocation } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/person-job-info/utils';
import { usePeoplePhoto } from '@aibees/elephant/feature/search-table-people/components/people-drawer/hooks/use-people-photo';
import {
  useCompanyVisibilityState,
  useDisplayCompanyName,
} from '@aibees/elephant/hooks';
import {
  CustomPopover,
  Icon,
  IconButton,
  Scrollbar,
  TextEllipsis,
  usePopover,
} from '@aibees/shared/components';
import { capitalize, classnames } from '@aibees/shared/utils';
import {
  Avatar,
  Box,
  Divider,
  Popover,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { CompanyInfoBadge } from '../company-info-badge/company-info-badge';
import { LimitTypography } from '../limit-typography';
import { PreviousEmploymentBadge } from '../previous-employment-badge';
import { SearchSocialMedia } from '../search-social-media';
import { SocialLink } from '../search-social-media-item';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleProfileDetailPopoverProps as Props } from './types';

const POPOVER_ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'center' as const,
};
const POPOVER_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'center' as const,
};
const POPOVER_SLOT_PROPS = { paper: { sx: SX.popover } };
const CUSTOM_POPOVER_ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const CUSTOM_POPOVER_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

const AvatarFallbackIcon = memo(() => (
  <Icon size={20} name="person-outline" color="grey.400" />
));
const PortraitIcon = memo(() => (
  <Icon name="portrait" color="grey.500" size={18} />
));
const MedalStarIcon = memo(() => (
  <Icon name="medal-star" color="grey.500" size={18} />
));
const LocationIcon = memo(() => (
  <Icon name="location" color="grey.500" size={18} />
));
const ApartmentIcon = memo(() => (
  <Icon name="apartment-rounded" color="grey.500" size={18} />
));
const CalendarIcon = memo(() => (
  <Icon name="calendar-view-month-outline" color="grey.500" size={18} />
));
const BusinessIcon = memo(() => (
  <Icon name="business" color="grey.500" size={18} />
));

const PrimaryDivider = memo(() => {
  const theme = useTheme();
  return <Divider color={theme.palette.primary[100]} />;
});
PrimaryDivider.displayName = 'PrimaryDivider';

export const PeopleProfileDetailPopover = memo(({ className, data }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [, photoSrc] = usePeoplePhoto(data);
  const fullWorkHistory = useWorkHistoryFullHistory(data);
  const allCurrentRoles = useWorkHistoryCurrentRoles(data);
  const companyDataState = useCompanyVisibilityState(data);
  const { hasOnlyCompanyName } = companyDataState;
  const roleName = data?.profile?.title;
  const { displayName: displayCompanyName, color: companyNameColor } =
    useDisplayCompanyName(data);

  const roleDuration = useMemo(
    () => getJobDuration(data?.statistics?.experience?.tenure?.title),
    [data?.statistics?.experience?.tenure?.title]
  );

  const open = Boolean(anchorEl);
  const companyPopoverState = usePopover();

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCompanyPopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      companyPopoverState.onOpen(event.currentTarget);
    },
    [companyPopoverState]
  );

  const hasCompanyDescription = !!data?.company?.summary?.description;

  return (
    <Styled.Wrapper
      className={classnames('PeopleProfileDetailPopover', className)}
      sx={SX.root}
    >
      <IconButton
        icon="notes-outline"
        sx={SX.box}
        onClick={handleClick}
        size="xsmall"
        variant="outlined"
        iconSize={12}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={POPOVER_ANCHOR_ORIGIN}
        transformOrigin={POPOVER_TRANSFORM_ORIGIN}
        slotProps={POPOVER_SLOT_PROPS}
      >
        {open && (
          <>
            <Stack direction="row" gap={0.5} alignItems="center" p={1.5}>
              <Avatar
                src={photoSrc}
                alt={data?.profile?.full_name ?? undefined}
                size={48}
                color="white"
                variant="rounded"
              >
                <AvatarFallbackIcon />
              </Avatar>
              <Stack justifyContent="space-between">
                <TextEllipsis
                  variant="h6"
                  color="grey.700"
                  fontWeight={700}
                  w="295px"
                  value={data?.profile?.full_name ?? undefined}
                />

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                >
                  <SearchSocialMedia
                    socialLink={data?.link as SocialLink}
                    hideLinkOutIcon={true}
                  />
                </Box>
              </Stack>
            </Stack>
            <PrimaryDivider />

            <Scrollbar sx={SX.scrollbar}>
              <Stack gap={1}>
                <Stack gap={1}>
                  {data?.profile?.summary && (
                    <Stack gap={1}>
                      <Typography
                        variant="body3"
                        fontWeight={700}
                        color="grey.700"
                      >
                        Profile Overview:
                      </Typography>
                      <Box>
                        <LimitTypography
                          value={data?.profile.summary}
                          wordLimit={15}
                          variant="body4"
                          fontWeight={400}
                          color="grey.700"
                        />
                      </Box>
                    </Stack>
                  )}
                  {hasCompanyDescription && (
                    <CompanyDescription
                      data={data?.company}
                      showCompanyInfoBadge
                      isDetailPopover
                      sx={SX.companyDescription}
                    />
                  )}
                </Stack>

                <Stack gap={1}>
                  {roleName && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <PreviousEmploymentBadge personData={data}>
                          <PortraitIcon />
                        </PreviousEmploymentBadge>
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Job Title:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body4"
                        fontWeight={400}
                        color="grey.700"
                      >
                        {roleName}
                      </Typography>
                    </Stack>
                  )}
                  {data?.profile?.headline && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <PreviousEmploymentBadge personData={data}>
                          <MedalStarIcon />
                        </PreviousEmploymentBadge>
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Headline:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body4"
                        fontWeight={400}
                        color="grey.700"
                      >
                        {data?.profile.headline}
                      </Typography>
                    </Stack>
                  )}
                  {data?.profile?.title && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <LocationIcon />
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Contact Location:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body4"
                        fontWeight={400}
                        color="grey.700"
                      >
                        {humanReadablePersonLocation(data?.location)}
                      </Typography>
                    </Stack>
                  )}
                  {data?.department?.departments && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <PreviousEmploymentBadge personData={data}>
                          <ApartmentIcon />
                        </PreviousEmploymentBadge>
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Department:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body4"
                        fontWeight={400}
                        color="grey.700"
                      >
                        {capitalize(data?.department?.departments[0])}
                      </Typography>
                    </Stack>
                  )}
                  {roleDuration && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <PreviousEmploymentBadge personData={data}>
                          <CalendarIcon />
                        </PreviousEmploymentBadge>
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Time in Role:
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body4"
                        fontWeight={400}
                        color="grey.700"
                      >
                        {roleDuration.fullText}
                      </Typography>
                    </Stack>
                  )}
                  {displayCompanyName && (
                    <Stack gap={1}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <CompanyInfoBadge personData={data}>
                          <BusinessIcon />
                        </CompanyInfoBadge>
                        <Typography
                          variant="body3"
                          fontWeight={700}
                          color="grey.700"
                        >
                          Company:
                        </Typography>
                      </Stack>
                      <Stack direction="row" gap={1}>
                        <Typography
                          variant="body4"
                          fontWeight={400}
                          color={companyNameColor}
                        >
                          {displayCompanyName}
                        </Typography>
                        {!hasOnlyCompanyName && fullWorkHistory.length > 1 && (
                          <Box
                            justifyContent="center"
                            alignItems="center"
                            sx={SX.badge}
                            onClick={handleCompanyPopoverOpen}
                          >
                            +{fullWorkHistory.length}
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Scrollbar>
          </>
        )}
      </Popover>

      <CustomPopover
        state={companyPopoverState}
        anchorOrigin={CUSTOM_POPOVER_ANCHOR_ORIGIN}
        transformOrigin={CUSTOM_POPOVER_TRANSFORM_ORIGIN}
        disableRestoreFocus
      >
        <JobHistory currentRoles={fullWorkHistory} />
      </CustomPopover>
    </Styled.Wrapper>
  );
});

PeopleProfileDetailPopover.displayName = 'PeopleProfileDetailPopover';
